import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Server, Activity, Clock, Search, Filter, RefreshCw,
  ChevronDown, ChevronUp, Cpu, MemoryStick, AlertTriangle,
  CheckCircle, XCircle, MinusCircle, HelpCircle, Wifi, WifiOff,
  X
} from 'lucide-react';
import logger from '../utils/logger';

/**
 * Configuration for all 16 Quantrolix ecosystem apps
 */
const APP_REGISTRY = [
  { id: 'quantrolix-launcher', name: 'Quantrolix Launcher', port: 3000, icon: '🚀', category: 'core' },
  { id: 'quantrolix-saas', name: 'QuantrolixAI SaaS', port: 3001, icon: '📊', category: 'core' },
  { id: 'fire-pos', name: 'Fire POS', port: 3008, icon: '🔥', category: 'restaurant' },
  { id: 'menumentor', name: 'Menu Mentor', port: 3005, icon: '🍽️', category: 'restaurant' },
  { id: 'qode-ide', name: 'Qode IDE', port: 8010, icon: '💻', category: 'dev-tools' },
  { id: 'tea-effects', name: 'Tea Effects', port: 3003, icon: '🍵', category: 'lifestyle' },
  { id: 'yieldvault', name: 'YieldVault', port: 3014, icon: '📦', category: 'business' },
  { id: 'kasa-controller', name: 'Kasa Controller', port: 3011, icon: '🏠', category: 'iot' },
  { id: 'architect-3d', name: 'Architect 3D', port: 8015, icon: '🏗️', category: 'creative' },
  { id: 'nexus-3d', name: 'Nexus 3D Maps', port: null, icon: '🗺️', category: 'creative' },
  { id: 'grocery-optimizer', name: 'Grocery Optimizer', port: 3013, icon: '🛒', category: 'lifestyle' },
  { id: 'motionforge', name: 'MotionForge', port: 3017, icon: '🎬', category: 'creative' },
  { id: 'couplescare', name: 'CouplesCare', port: 8001, icon: '❤️', category: 'lifestyle' },
  { id: 'bourbon-hunter', name: 'Bourbon Hunter', port: null, icon: '🥃', category: 'lifestyle' },
  { id: 'phantomcast', name: 'PhantomCast', port: 3004, icon: '📡', category: 'streaming' },
  { id: 'comm-hub', name: 'Comm Hub', port: 3030, icon: '🔗', category: 'core' },
];

const STATUS_CONFIG = {
  healthy: { color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.15)', borderColor: 'rgba(34, 197, 94, 0.4)', label: 'Healthy', Icon: CheckCircle },
  degraded: { color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.15)', borderColor: 'rgba(234, 179, 8, 0.4)', label: 'Degraded', Icon: MinusCircle },
  down: { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.15)', borderColor: 'rgba(239, 68, 68, 0.4)', label: 'Down', Icon: XCircle },
  unknown: { color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.15)', borderColor: 'rgba(107, 114, 128, 0.4)', label: 'Unknown', Icon: HelpCircle },
};

const POLL_INTERVAL = 30000; // 30 seconds

/**
 * Generates realistic mock health data for all 16 apps.
 * In production, this would be replaced by real PM2/health endpoint data.
 */
function generateMockHealthData() {
  const now = Date.now();
  return APP_REGISTRY.map((app) => {
    // Simulate mostly healthy with occasional degraded/down
    const rand = Math.random();
    let status = 'healthy';
    if (rand > 0.92) status = 'down';
    else if (rand > 0.82) status = 'degraded';

    const uptimeBase = status === 'healthy' ? 99.0 : status === 'degraded' ? 95.0 : 0;
    const uptimeVariance = status === 'healthy' ? Math.random() * 1.0 : Math.random() * 4.0;
    const uptime = status === 'down' ? 0 : Math.min(100, uptimeBase + uptimeVariance);

    // Random deploy time within last 7 days
    const deployOffset = Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000);
    const lastDeploy = new Date(now - deployOffset).toISOString();

    const memoryUsage = status === 'down' ? 0 : Math.floor(40 + Math.random() * 120);
    const cpuUsage = status === 'down' ? 0 : parseFloat((Math.random() * 15).toFixed(1));
    const errorCount = status === 'healthy' ? Math.floor(Math.random() * 3) : status === 'degraded' ? Math.floor(5 + Math.random() * 20) : 0;
    const responseTime = status === 'down' ? null : Math.floor(50 + Math.random() * 200);

    return {
      id: app.id,
      status,
      uptime: parseFloat(uptime.toFixed(2)),
      lastDeploy,
      memoryMb: memoryUsage,
      cpuPercent: cpuUsage,
      errorCount,
      responseTimeMs: responseTime,
      pid: status === 'down' ? null : Math.floor(10000 + Math.random() * 50000),
      restarts: Math.floor(Math.random() * 5),
    };
  });
}

/**
 * Format a timestamp into a relative time string (e.g., "2h ago", "3d ago")
 */
function formatRelativeTime(isoString) {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

/**
 * AppDeploymentStatus - Shows health indicators for all 16 Quantrolix apps
 */
const AppDeploymentStatus = () => {
  const [healthData, setHealthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const expandedRef = useRef(null);

  /**
   * Fetch health data from Comm Hub or fall back to mock data
   */
  const fetchHealthData = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);

      const response = await fetch('https://apps.quantrolix.ai/api/comm-hub/api/activity', {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' },
      });
      clearTimeout(timeout);

      if (response.ok) {
        const data = await response.json();
        // If the comm hub returns activity data, map it to our format
        if (data && (Array.isArray(data) || data.agents || data.services)) {
          const services = Array.isArray(data) ? data : (data.services || data.agents || []);
          const mapped = APP_REGISTRY.map((app) => {
            const match = services.find(
              (s) => s.project === app.id || s.name === app.id || s.claudeId === app.id
            );
            return {
              id: app.id,
              status: match ? (match.status === 'active' ? 'healthy' : 'degraded') : 'unknown',
              uptime: match ? 99.9 : 0,
              lastDeploy: match?.lastSeen || match?.timestamp || null,
              memoryMb: match?.memory || Math.floor(40 + Math.random() * 120),
              cpuPercent: match?.cpu || parseFloat((Math.random() * 10).toFixed(1)),
              errorCount: match?.errors || 0,
              responseTimeMs: match?.responseTime || Math.floor(50 + Math.random() * 150),
              pid: match?.pid || null,
              restarts: match?.restarts || 0,
            };
          });
          setHealthData(mapped);
          setIsConnected(true);
          setError(null);
          setLastChecked(new Date());
          setLoading(false);
          return;
        }
      }
      // If response not useful, fall back to mock
      throw new Error('Comm Hub data not in expected format');
    } catch (err) {
      logger.debug('Using mock health data:', err.message);
      // Use mock data as fallback
      setHealthData(generateMockHealthData());
      setIsConnected(false);
      setError(null); // Don't show error for expected fallback
      setLastChecked(new Date());
      setLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchHealthData();

    intervalRef.current = setInterval(fetchHealthData, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchHealthData]);

  // Close expanded card on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedRef.current && !expandedRef.current.contains(e.target)) {
        setExpandedApp(null);
      }
    };
    if (expandedApp) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedApp]);

  /**
   * Build the merged list of app + health data, then apply filters
   */
  const getFilteredApps = useCallback(() => {
    const merged = APP_REGISTRY.map((app) => {
      const health = healthData.find((h) => h.id === app.id) || {
        status: 'unknown',
        uptime: 0,
        lastDeploy: null,
        memoryMb: 0,
        cpuPercent: 0,
        errorCount: 0,
        responseTimeMs: null,
        pid: null,
        restarts: 0,
      };
      return { ...app, ...health };
    });

    return merged.filter((app) => {
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
      const matchesSearch =
        !searchQuery ||
        app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [healthData, statusFilter, searchQuery]);

  const filteredApps = getFilteredApps();

  // Summary counts
  const statusCounts = {
    healthy: healthData.filter((h) => h.status === 'healthy').length,
    degraded: healthData.filter((h) => h.status === 'degraded').length,
    down: healthData.filter((h) => h.status === 'down').length,
    unknown: healthData.filter((h) => h.status === 'unknown').length,
  };

  const handleCardClick = (appId) => {
    setExpandedApp(expandedApp === appId ? null : appId);
  };

  if (loading) {
    return (
      <div className="deploy-status-widget">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0' }}>
          <div className="loading-spinner" style={{ width: '36px', height: '36px', marginRight: '12px' }}></div>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem' }}>Loading deployment status...</span>
        </div>
        <style>{widgetStyles}</style>
      </div>
    );
  }

  return (
    <div className="deploy-status-widget">
      {/* Widget Header */}
      <div className="dsw-header">
        <div className="dsw-header-left">
          <Server style={{ width: 22, height: 22, color: '#00d4ff' }} />
          <h2 className="dsw-title">App Deployment Status</h2>
          <span className="dsw-app-count">{APP_REGISTRY.length} apps</span>
        </div>
        <div className="dsw-header-right">
          <div className="dsw-connection-badge" data-connected={isConnected}>
            {isConnected ? <Wifi style={{ width: 14, height: 14 }} /> : <WifiOff style={{ width: 14, height: 14 }} />}
            <span>{isConnected ? 'Live' : 'Mock Data'}</span>
          </div>
          {lastChecked && (
            <span className="dsw-last-checked">
              <Clock style={{ width: 13, height: 13 }} />
              {lastChecked.toLocaleTimeString()}
            </span>
          )}
          <button className="dsw-refresh-btn" onClick={fetchHealthData} title="Refresh now">
            <RefreshCw style={{ width: 15, height: 15 }} />
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="dsw-summary-bar">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const count = statusCounts[key] || 0;
          const isActive = statusFilter === key;
          return (
            <button
              key={key}
              className={`dsw-summary-pill ${isActive ? 'active' : ''}`}
              style={{
                '--pill-color': config.color,
                '--pill-bg': config.bgColor,
                '--pill-border': config.borderColor,
              }}
              onClick={() => setStatusFilter(isActive ? 'all' : key)}
              title={`Filter: ${config.label}`}
            >
              <config.Icon style={{ width: 14, height: 14, color: config.color }} />
              <span className="dsw-pill-count">{count}</span>
              <span className="dsw-pill-label">{config.label}</span>
            </button>
          );
        })}
      </div>

      {/* Search / Filter Bar */}
      <div className="dsw-filter-bar">
        <div className="dsw-search-wrap">
          <Search style={{ width: 15, height: 15, color: 'rgba(255,255,255,0.4)' }} />
          <input
            type="text"
            placeholder="Search apps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="dsw-search-input"
          />
          {searchQuery && (
            <button className="dsw-search-clear" onClick={() => setSearchQuery('')} title="Clear search">
              <X style={{ width: 14, height: 14 }} />
            </button>
          )}
        </div>
        {statusFilter !== 'all' && (
          <button className="dsw-clear-filter" onClick={() => setStatusFilter('all')}>
            <Filter style={{ width: 13, height: 13 }} />
            Clear filter
          </button>
        )}
      </div>

      {/* App Grid */}
      <div className="dsw-grid">
        {filteredApps.length === 0 ? (
          <div className="dsw-empty">
            <AlertTriangle style={{ width: 28, height: 28, color: 'rgba(255,255,255,0.3)' }} />
            <p>No apps match your current filters.</p>
          </div>
        ) : (
          filteredApps.map((app) => {
            const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.unknown;
            const isExpanded = expandedApp === app.id;

            return (
              <div
                key={app.id}
                ref={isExpanded ? expandedRef : undefined}
                className={`dsw-card ${isExpanded ? 'expanded' : ''}`}
                style={{
                  '--card-accent': cfg.color,
                  '--card-bg': cfg.bgColor,
                  '--card-border': cfg.borderColor,
                }}
                onClick={() => handleCardClick(app.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(app.id);
                  }
                }}
                aria-expanded={isExpanded}
                aria-label={`${app.name} - ${cfg.label}`}
              >
                {/* Status accent line */}
                <div className="dsw-card-accent" />

                {/* Card Header */}
                <div className="dsw-card-header">
                  <span className="dsw-card-icon">{app.icon}</span>
                  <div className="dsw-card-info">
                    <span className="dsw-card-name">{app.name}</span>
                    {app.port && <span className="dsw-card-port">:{app.port}</span>}
                  </div>
                  <div className="dsw-card-status-dot" title={cfg.label}>
                    <cfg.Icon style={{ width: 16, height: 16, color: cfg.color }} />
                  </div>
                </div>

                {/* Card Metrics Row */}
                <div className="dsw-card-metrics">
                  <div className="dsw-metric">
                    <span className="dsw-metric-label">Uptime</span>
                    <span className="dsw-metric-value" style={{ color: app.uptime >= 99 ? '#22c55e' : app.uptime >= 95 ? '#eab308' : '#ef4444' }}>
                      {app.status === 'down' ? '--' : `${app.uptime}%`}
                    </span>
                  </div>
                  <div className="dsw-metric">
                    <span className="dsw-metric-label">Deployed</span>
                    <span className="dsw-metric-value">
                      {formatRelativeTime(app.lastDeploy)}
                    </span>
                  </div>
                </div>

                {/* Uptime Bar */}
                <div className="dsw-uptime-bar">
                  <div
                    className="dsw-uptime-fill"
                    style={{
                      width: `${Math.min(app.uptime, 100)}%`,
                      background: app.uptime >= 99 ? '#22c55e' : app.uptime >= 95 ? '#eab308' : '#ef4444',
                    }}
                  />
                </div>

                {/* Expand indicator */}
                <div className="dsw-expand-indicator">
                  {isExpanded ? (
                    <ChevronUp style={{ width: 14, height: 14, color: 'rgba(255,255,255,0.4)' }} />
                  ) : (
                    <ChevronDown style={{ width: 14, height: 14, color: 'rgba(255,255,255,0.4)' }} />
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="dsw-card-details" onClick={(e) => e.stopPropagation()}>
                    <div className="dsw-details-grid">
                      <div className="dsw-detail-item">
                        <Cpu style={{ width: 14, height: 14, color: '#3b82f6' }} />
                        <span className="dsw-detail-label">CPU</span>
                        <span className="dsw-detail-value">{app.cpuPercent}%</span>
                      </div>
                      <div className="dsw-detail-item">
                        <MemoryStick style={{ width: 14, height: 14, color: '#a855f7' }} />
                        <span className="dsw-detail-label">Memory</span>
                        <span className="dsw-detail-value">{app.memoryMb} MB</span>
                      </div>
                      <div className="dsw-detail-item">
                        <AlertTriangle style={{ width: 14, height: 14, color: app.errorCount > 5 ? '#ef4444' : '#eab308' }} />
                        <span className="dsw-detail-label">Errors</span>
                        <span className="dsw-detail-value" style={{ color: app.errorCount > 5 ? '#ef4444' : 'inherit' }}>
                          {app.errorCount}
                        </span>
                      </div>
                      <div className="dsw-detail-item">
                        <Activity style={{ width: 14, height: 14, color: '#06b6d4' }} />
                        <span className="dsw-detail-label">Response</span>
                        <span className="dsw-detail-value">
                          {app.responseTimeMs !== null ? `${app.responseTimeMs}ms` : '--'}
                        </span>
                      </div>
                    </div>
                    <div className="dsw-details-footer">
                      <span>PID: {app.pid || '--'}</span>
                      <span>Restarts: {app.restarts}</span>
                      {app.port && <span>Port: {app.port}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <style>{widgetStyles}</style>
    </div>
  );
};

/**
 * All styles scoped to the .deploy-status-widget container
 * Follows the glassmorphism + quantum design from the rest of the dashboard
 */
const widgetStyles = `
  .deploy-status-widget {
    background: rgba(10, 10, 15, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 20px;
    padding: 24px;
    margin-top: 24px;
    animation: dswFadeIn 0.4s ease-out;
  }

  @keyframes dswFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Header */
  .dsw-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 12px;
  }

  .dsw-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .dsw-title {
    font-family: 'Orbitron', monospace;
    font-size: 1.15rem;
    font-weight: 600;
    background: linear-gradient(135deg, #667eea, #00d4ff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .dsw-app-count {
    font-size: 0.75rem;
    color: rgba(255,255,255,0.4);
    background: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 8px;
    padding: 2px 8px;
  }

  .dsw-header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .dsw-connection-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.75rem;
    padding: 3px 10px;
    border-radius: 6px;
    font-weight: 500;
  }

  .dsw-connection-badge[data-connected="true"] {
    color: #22c55e;
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.3);
  }

  .dsw-connection-badge[data-connected="false"] {
    color: #eab308;
    background: rgba(234, 179, 8, 0.1);
    border: 1px solid rgba(234, 179, 8, 0.3);
  }

  .dsw-last-checked {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.75rem;
    color: rgba(255,255,255,0.4);
  }

  .dsw-refresh-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 8px;
    color: #00d4ff;
    cursor: pointer;
    transition: all 0.2s;
  }

  .dsw-refresh-btn:hover {
    background: rgba(0, 212, 255, 0.2);
    transform: rotate(90deg);
  }

  /* Summary Bar */
  .dsw-summary-bar {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  .dsw-summary-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 20px;
    background: var(--pill-bg);
    border: 1px solid var(--pill-border);
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.8rem;
    color: rgba(255,255,255,0.8);
  }

  .dsw-summary-pill:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  .dsw-summary-pill.active {
    box-shadow: 0 0 0 2px var(--pill-color), 0 4px 16px rgba(0,0,0,0.3);
  }

  .dsw-pill-count {
    font-weight: 700;
    color: var(--pill-color);
    font-family: 'Orbitron', monospace;
    font-size: 0.85rem;
  }

  .dsw-pill-label {
    color: rgba(255,255,255,0.6);
    font-size: 0.75rem;
  }

  /* Filter / Search Bar */
  .dsw-filter-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
  }

  .dsw-search-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    max-width: 300px;
    padding: 7px 12px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 10px;
    transition: border-color 0.2s;
  }

  .dsw-search-wrap:focus-within {
    border-color: rgba(0, 212, 255, 0.5);
  }

  .dsw-search-input {
    background: none;
    border: none;
    outline: none;
    color: #fff;
    font-size: 0.85rem;
    width: 100%;
    font-family: inherit;
  }

  .dsw-search-input::placeholder {
    color: rgba(255,255,255,0.3);
  }

  .dsw-search-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: rgba(255,255,255,0.4);
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    transition: color 0.2s;
  }

  .dsw-search-clear:hover {
    color: rgba(255,255,255,0.8);
  }

  .dsw-clear-filter {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 8px;
    color: #ef4444;
    cursor: pointer;
    font-size: 0.78rem;
    transition: all 0.2s;
  }

  .dsw-clear-filter:hover {
    background: rgba(239, 68, 68, 0.2);
  }

  /* App Grid */
  .dsw-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 14px;
  }

  .dsw-empty {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 20px;
    color: rgba(255,255,255,0.4);
    font-size: 0.9rem;
  }

  /* App Card */
  .dsw-card {
    position: relative;
    background: rgba(15, 15, 25, 0.7);
    border: 1px solid var(--card-border);
    border-radius: 14px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.25s ease;
    overflow: hidden;
  }

  .dsw-card:hover {
    transform: translateY(-3px);
    border-color: var(--card-accent);
    box-shadow: 0 8px 24px rgba(0,0,0,0.3), 0 0 0 1px var(--card-border);
  }

  .dsw-card.expanded {
    border-color: var(--card-accent);
    box-shadow: 0 0 20px color-mix(in srgb, var(--card-accent) 30%, transparent);
  }

  .dsw-card:focus-visible {
    outline: 2px solid #00d4ff;
    outline-offset: 2px;
  }

  /* Accent line at top of card */
  .dsw-card-accent {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--card-accent);
    border-radius: 14px 14px 0 0;
  }

  /* Card Header */
  .dsw-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  .dsw-card-icon {
    font-size: 1.4rem;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(102, 126, 234, 0.1);
    border-radius: 10px;
    flex-shrink: 0;
  }

  .dsw-card-info {
    flex: 1;
    min-width: 0;
  }

  .dsw-card-name {
    display: block;
    font-size: 0.88rem;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dsw-card-port {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.35);
    font-family: 'Orbitron', monospace;
  }

  .dsw-card-status-dot {
    flex-shrink: 0;
  }

  /* Metrics Row */
  .dsw-card-metrics {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .dsw-metric {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .dsw-metric-label {
    font-size: 0.68rem;
    color: rgba(255,255,255,0.4);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .dsw-metric-value {
    font-size: 0.82rem;
    font-weight: 600;
    color: rgba(255,255,255,0.85);
    font-family: 'Orbitron', monospace;
  }

  /* Uptime Bar */
  .dsw-uptime-bar {
    width: 100%;
    height: 4px;
    background: rgba(255,255,255,0.08);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 6px;
  }

  .dsw-uptime-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.6s ease;
  }

  /* Expand Indicator */
  .dsw-expand-indicator {
    display: flex;
    justify-content: center;
    padding-top: 4px;
  }

  /* Expanded Details */
  .dsw-card-details {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(102, 126, 234, 0.15);
    animation: dswSlideDown 0.25s ease-out;
  }

  @keyframes dswSlideDown {
    from { opacity: 0; max-height: 0; }
    to { opacity: 1; max-height: 200px; }
  }

  .dsw-details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 10px;
  }

  .dsw-detail-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.78rem;
  }

  .dsw-detail-label {
    color: rgba(255,255,255,0.45);
    flex-shrink: 0;
  }

  .dsw-detail-value {
    color: rgba(255,255,255,0.85);
    font-weight: 600;
    font-family: 'Orbitron', monospace;
    font-size: 0.75rem;
    margin-left: auto;
  }

  .dsw-details-footer {
    display: flex;
    gap: 14px;
    font-size: 0.7rem;
    color: rgba(255,255,255,0.3);
    padding-top: 8px;
    border-top: 1px solid rgba(102, 126, 234, 0.08);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .dsw-grid {
      grid-template-columns: 1fr;
    }

    .dsw-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .dsw-summary-bar {
      flex-wrap: wrap;
    }

    .dsw-filter-bar {
      flex-direction: column;
      align-items: stretch;
    }

    .dsw-search-wrap {
      max-width: 100%;
    }
  }

  @media (min-width: 769px) and (max-width: 1200px) {
    .dsw-grid {
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    }
  }

  @media (min-width: 1600px) {
    .dsw-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }
`;

export default AppDeploymentStatus;
